import { toast } from 'react-hot-toast';
import { Form } from 'semantic-ui-react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';

import * as S from './styles';
import { Modal } from '~/components';
import { Button } from '~/components';
import { 
  InvestorFeedbackBidStatus,
  InvestorFeedbackRating,
  InvestorFeedbackButtonText,
  InvestorFeedbackSelection
} from '~/constants/project-investor-feedback';
import BidFeedbackCheckbox from './BidFeedbackCheckbox';
import BidFeedbackNotes from './BidFeedbackNotes';
import { useProjectContext } from '~/hooks';
import { useForm } from '~/hooks/providers/FormContextProvider';
import { useSaveInvestorFeedback } from '~/mutations/project/useSaveInvestorFeedback';
import { declineProjectBid } from '~/api/projectBids';
import { logUserActivity, UserActivityTypes } from '~/api/user';
import { ToastDuration } from '~/constants/toasts';
import { Project } from '~/types/project';

interface BidFeedbackSelectionFormProps {
  onGoBack: () => void,
  closeModal: () => void,
  bidStatus: string | null,
  project?: Project,
  declineMutationFn?: (formState: any) => Promise<any>,
  onDeclineSuccess?: () => void,
  declineTitle?: string,
  declineDescription?: string,
}

interface BidFeedbackSelectionFormState {
  feedbackSelection: typeof InvestorFeedbackSelection[keyof typeof InvestorFeedbackSelection][];
  feedbackNotes: string;
  feedbackRating: typeof InvestorFeedbackRating[keyof typeof InvestorFeedbackRating];
}

export default function BidFeedbackSelectionForm({
  onGoBack,
  closeModal,
  bidStatus,
  project,
  declineMutationFn,
  onDeclineSuccess,
  declineTitle,
  declineDescription,
}: BidFeedbackSelectionFormProps) {
  const { formState } = useForm<BidFeedbackSelectionFormState>();
  const { project: contextProject } = useProjectContext();
  if (!project) {
    project = contextProject;
  }
  const history = useHistory();
  const investorId = project?.investorId;
  const portfolioId = project?.portfolioId;

  const { mutate: saveFeedback, status: saveFeedbackStatus } = useSaveInvestorFeedback();
  const { mutateAsync: executeDecline, status: declineStatus } = useMutation(() => (
    declineMutationFn
      ? declineMutationFn(formState)
      : declineProjectBid(project!.id, { ...formState, investorId })
  ));

  const handleBidDecline = async () => {
    try {
      const result = await executeDecline();

      logUserActivity({
        activityType: UserActivityTypes.DECLINED_TO_BID,
        projectId: declineMutationFn ? null : (project?.id ?? null),
        portfolioId: portfolioId ?? null,
      });

      toast.success('Thank you for sharing your feedback.');

      if (onDeclineSuccess) {
        onDeclineSuccess();
      } else if (portfolioId && !result?.isFinalProject) {
        window.location.reload();
      } else {
        history.push('/dashboard');
      }

      closeModal();
    } catch (error) {
      console.error(error);
      toast.error(
        <div>
          <p>
            We ran into an error while attempting to decline your bid. Please try again, and if you continue to run into issues, contact{' '}
            <a href="mailto:support@conductor.solar">support@conductor.solar</a>.
          </p>
        </div>,
        { duration: ToastDuration.LONG },
      );
    }
  };
    
  return (
    <>
      <Modal.Title>{declineTitle ?? 'Capturing project feedback'}</Modal.Title>
      <Modal.Content>
        {bidStatus === InvestorFeedbackBidStatus.BID_DECLINED && (
          <p style={{ margin: '0 0 var(--medium) 0'}}>
            {declineDescription ?? 'We’d like to hear what you think about this project and why you weren’t interested in bidding. Please complete the form below. We’ll share your feedback with the developer and review your answers to make sure we’re matching you with opportunities that fit your needs.'}
          </p>
        )}
        <Form>
          <S.FeedbackContainer>
            <h3>What makes this a less-than great fit?</h3>
            <p>Select all that apply.</p>
            <BidFeedbackCheckbox  />
            <BidFeedbackNotes />
          </S.FeedbackContainer>
        </Form>
      </Modal.Content>
      <Modal.Footer style={{ justifyContent: bidStatus !== InvestorFeedbackBidStatus.BID_DECLINED ? 'space-between' : 'flex-end'}}>
        {bidStatus === InvestorFeedbackBidStatus.BID_DECLINED 
          ? <Button primary outlined onClick={() => closeModal()}>Cancel</Button>
          : <Button primary outlined onClick={() => onGoBack()}>Go back</Button>
        }
        <Button
          primary
          onClick={() => {
           if (formState.feedbackRating === InvestorFeedbackRating.DECLINED_TO_BID) {
              handleBidDecline();
            } else if (formState.feedbackRating) {
              saveFeedback(formState, { onSuccess: closeModal });
            }
          }}
          loading={saveFeedbackStatus === 'loading' || declineStatus === 'loading'}
          disabled={
            (!formState.feedbackSelection?.length && !formState.feedbackNotes) 
              || (formState.feedbackSelection.includes(InvestorFeedbackSelection.OTHER) && !formState.feedbackNotes)
              || saveFeedbackStatus === 'loading' 
              || declineStatus === 'loading'
            }
          >
            {bidStatus === InvestorFeedbackBidStatus.BID_DECLINED ? InvestorFeedbackButtonText.DECLINE_AND_SUBMIT : InvestorFeedbackButtonText.SUBMIT_FEEDBACK}
        </Button>
      </Modal.Footer>
    </>
  );
}